import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Building2,
  MoreVertical,
  Package,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { licensingApi, isPlatformAdminRequiredError } from '@/api/licensing';
import { orgsApi } from '@/api/orgs';
import { Button, Badge, Loading, EmptyState } from '@/components/ui';
import Modal from '@/components/ui/Modal';
import { cn, hebrewLabel, ORG_STATUS_LABELS } from '@/utils/helpers';
import { formatRelative } from '@/utils/date';
import type { OrgListItem } from '@/types';
import type { LimitKey } from '@/types/licensing';

const STATUS_FILTERS = [
  { value: '', label: 'הכל' },
  { value: 'active', label: 'פעיל' },
  { value: 'inactive', label: 'לא פעיל' },
  { value: 'suspended', label: 'מושעה' },
];

const STATUS_BADGE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  inactive: 'neutral',
  suspended: 'danger',
};

function LicenseCell({
  systemSubs,
  limits,
  limitsDenied,
}: {
  systemSubs: NonNullable<OrgListItem['org_system_subscriptions']>;
  limits?: Record<LimitKey, { used: number; effective: number }>;
  limitsDenied?: boolean;
}) {
  const jumpstart = systemSubs.find((s) => s.system_code === 'jumpstart');

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {jumpstart ? (
        <PlanBadge planCode={jumpstart.plan_code} status={jumpstart.status} />
      ) : (
        <span className="text-xs text-ink-muted">—</span>
      )}
      {limitsDenied ? (
        <span className="text-[11px] text-amber-400" title="Requires is_platform_admin() or is_super_admin()">
          No admin access
        </span>
      ) : limits ? (
        <span className="num text-[11px] text-ink-secondary">
          U {formatCompactLimit(limits.users.used, limits.users.effective, 'users')}
          <span className="mx-1 text-ink-muted">·</span>
          WA {formatCompactLimit(limits.whatsapp_devices.used, limits.whatsapp_devices.effective, 'whatsapp_devices')}
          <span className="mx-1 text-ink-muted">·</span>
          {formatCompactLimit(limits.storage_gb.used, limits.storage_gb.effective, 'storage_gb')}
        </span>
      ) : null}
    </div>
  );
}

function formatCompactLimit(used: number, effective: number, key: LimitKey): string {
  const usedLabel = key === 'storage_gb' ? `${used}GB` : String(used);
  if (effective === -1) return `${usedLabel}/∞`;
  const effectiveLabel = key === 'storage_gb' ? `${effective}GB` : String(effective);
  return `${usedLabel}/${effectiveLabel}`;
}

const PLAN_BADGE_COLOR: Record<string, string> = {
  active: 'bg-teal/15 text-teal border border-teal/30',
  trial: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  suspended: 'bg-red-500/15 text-red-400 border border-red-500/30',
  cancelled: 'bg-navy-hover text-ink-muted border border-navy-border',
};

function PlanBadge({ planCode, status }: { planCode: string; status: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        PLAN_BADGE_COLOR[status] ?? PLAN_BADGE_COLOR.cancelled
      )}
      title={`Jumpstart · ${planCode} · ${status}`}
    >
      {planCode}
    </span>
  );
}

// ─── Row actions menu ────────────────────────────────────────────────────────

type ConfirmState =
  | { kind: 'archive'; org: OrgListItem }
  | { kind: 'unarchive'; org: OrgListItem }
  | { kind: 'delete'; org: OrgListItem }
  | null;

function canHardDelete(org: OrgListItem): boolean {
  const hasUsers = (org.org_users?.length ?? 0) > 0;
  const hasSysSubs = (org.org_system_subscriptions?.length ?? 0) > 0;
  const hasProductSubs = (org.product_subscriptions?.length ?? 0) > 0;
  return !hasUsers && !hasSysSubs && !hasProductSubs;
}

function RowActions({
  org,
  onAction,
}: {
  org: OrgListItem;
  onAction: (state: ConfirmState) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const isArchived = Boolean(org.archived_at);
  const deletable = canHardDelete(org);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rounded-md p-1.5 text-ink-muted transition-colors hover:bg-navy-hover hover:text-ink-primary"
        aria-label="פעולות"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div
          className="absolute end-0 top-full z-20 mt-1 w-44 overflow-hidden rounded-lg border border-navy-border bg-navy-card shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {isArchived ? (
            <button
              onClick={() => {
                setOpen(false);
                onAction({ kind: 'unarchive', org });
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-right text-xs text-ink-primary hover:bg-navy-hover"
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
              שחזר מארכיון
            </button>
          ) : (
            <button
              onClick={() => {
                setOpen(false);
                onAction({ kind: 'archive', org });
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-right text-xs text-ink-primary hover:bg-navy-hover"
            >
              <Archive className="h-3.5 w-3.5" />
              העבר לארכיון
            </button>
          )}
          <button
            disabled={!deletable}
            title={deletable ? undefined : 'יש למחוק משתמשים ומנויים לפני מחיקה'}
            onClick={() => {
              if (!deletable) return;
              setOpen(false);
              onAction({ kind: 'delete', org });
            }}
            className={cn(
              'flex w-full items-center gap-2 border-t border-navy-border px-3 py-2 text-right text-xs',
              deletable
                ? 'text-red-400 hover:bg-red-500/10'
                : 'cursor-not-allowed text-ink-muted'
            )}
          >
            <Trash2 className="h-3.5 w-3.5" />
            מחק לצמיתות
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function OrgsListPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: orgsData, isLoading } = useQuery({
    queryKey: ['admin-orgs', statusFilter],
    queryFn: () => orgsApi.list({ status: statusFilter || undefined }),
    staleTime: 30_000,
  });

  const allOrgs = orgsData?.items ?? [];
  const filtered = allOrgs.filter(
    (org) =>
      !search ||
      org.name.toLowerCase().includes(search.toLowerCase()) ||
      org.email?.includes(search)
  );
  const orgIds = filtered.map((org) => org.id);

  const { data: effectiveLimitsByOrg, error: limitsError, isError: limitsIsError } = useQuery({
    queryKey: ['admin-org-effective-limits', orgIds],
    queryFn: () => licensingApi.listEffectiveLimitsForOrgs(orgIds),
    enabled: orgIds.length > 0,
    staleTime: 15_000,
    retry: false,
  });
  const limitsDenied = limitsIsError && isPlatformAdminRequiredError(limitsError);

  const totalActive = allOrgs.filter((org) => org.status === 'active').length;
  const totalWithSubs = allOrgs.filter((org) =>
    org.product_subscriptions?.some((sub) => sub.status === 'active')
  ).length;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });

  const archiveM = useMutation({
    mutationFn: (orgId: string) => orgsApi.archive(orgId),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
    onError: (e: Error) => setErrorMsg(e.message),
  });
  const unarchiveM = useMutation({
    mutationFn: (orgId: string) => orgsApi.unarchive(orgId),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
    onError: (e: Error) => setErrorMsg(e.message),
  });
  const deleteM = useMutation({
    mutationFn: (orgId: string) => orgsApi.hardDelete(orgId),
    onSuccess: () => {
      invalidate();
      setConfirm(null);
    },
    onError: (e: Error) => setErrorMsg(e.message),
  });

  const confirmBusy = archiveM.isPending || unarchiveM.isPending || deleteM.isPending;

  const runConfirm = () => {
    if (!confirm) return;
    setErrorMsg(null);
    if (confirm.kind === 'archive') archiveM.mutate(confirm.org.id);
    else if (confirm.kind === 'unarchive') unarchiveM.mutate(confirm.org.id);
    else deleteM.mutate(confirm.org.id);
  };

  return (
    <div dir="rtl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">ארגונים</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            {orgsData?.total ?? 0} ארגונים &middot; {totalActive} פעילים &middot; {totalWithSubs} עם מנויים
          </p>
        </div>
        <Button size="sm">
          <Plus className="h-4 w-4" />
          ארגון חדש
        </Button>
      </div>

      {limitsDenied && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
          אין הרשאת platform admin — עמודת הלימיטים ריקה. נדרש is_platform_admin() / is_super_admin().
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <input
            type="text"
            placeholder="חיפוש לפי שם או אימייל..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-full rounded-lg border border-navy-border bg-navy-card pe-10 ps-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
          />
        </div>

        <div className="flex gap-1.5">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={cn(
                'h-8 rounded-full px-3 text-xs font-medium transition-colors',
                statusFilter === filter.value
                  ? 'bg-gold text-navy'
                  : 'border border-navy-border bg-navy-card text-ink-secondary hover:text-ink-primary'
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <Loading text="טוען ארגונים..." />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Building2 className="h-10 w-10" />}
          title="לא נמצאו ארגונים"
          description={search ? 'נסה לשנות את החיפוש' : 'הוסף ארגון ראשון כדי להתחיל'}
        />
      ) : (
        <div className="card overflow-visible">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy-border bg-navy/30">
                <th className="px-5 py-3 text-center text-xs font-semibold text-ink-muted">ארגון</th>
                <th className="hidden px-5 py-3 text-center text-xs font-semibold text-ink-muted md:table-cell">אימייל</th>
                <th className="hidden px-5 py-3 text-center text-xs font-semibold text-ink-muted lg:table-cell">טלפון</th>
                <th className="px-5 py-3 text-center text-xs font-semibold text-ink-muted">
                  <span className="inline-flex items-center justify-center gap-1.5">
                    <Package className="h-3.5 w-3.5" />
                    License
                  </span>
                </th>
                <th className="hidden px-5 py-3 text-center text-xs font-semibold text-ink-muted xl:table-cell">
                  <span className="inline-flex items-center justify-center gap-1.5">
                    <Users className="h-3.5 w-3.5" />
                    משתמשים
                  </span>
                </th>
                <th className="px-5 py-3 text-center text-xs font-semibold text-ink-muted">סטטוס</th>
                <th className="hidden px-5 py-3 text-center text-xs font-semibold text-ink-muted lg:table-cell">נוצר</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-ink-muted">
                  <span className="sr-only">פעולות</span>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-navy-border">
              {filtered.map((org) => {
                const orgLimits = effectiveLimitsByOrg?.[org.id];
                const isArchived = Boolean(org.archived_at);

                return (
                  <tr
                    key={org.id}
                    className={cn(
                      'group transition-colors hover:bg-navy-hover',
                      isArchived && 'opacity-60'
                    )}
                  >
                    <td className="px-5 py-3.5">
                      <Link to={`/orgs/${org.id}`} className="flex items-center gap-3">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gold/10 text-sm font-bold text-gold transition-colors group-hover:bg-gold/20">
                          {org.name.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <span className="flex max-w-[220px] items-center gap-1.5 truncate font-medium text-ink-primary transition-colors hover:text-gold">
                            <span className="truncate">{org.name}</span>
                            {isArchived && (
                              <Archive className="h-3 w-3 shrink-0 text-ink-muted" aria-label="בארכיון" />
                            )}
                          </span>
                          {org.website && (
                            <span className="block max-w-[200px] truncate text-[11px] text-ink-muted">
                              {org.website}
                            </span>
                          )}
                        </div>
                      </Link>
                    </td>

                    <td className="hidden px-5 py-3.5 text-xs text-ink-secondary num md:table-cell">
                      {org.email ?? '—'}
                    </td>

                    <td className="hidden px-5 py-3.5 text-xs text-ink-secondary num lg:table-cell">
                      {org.phone ?? '—'}
                    </td>

                    <td className="px-5 py-3.5">
                      <LicenseCell
                        systemSubs={org.org_system_subscriptions ?? []}
                        limits={orgLimits}
                        limitsDenied={limitsDenied}
                      />
                    </td>

                    <td className="hidden px-5 py-3.5 xl:table-cell">
                      {org.org_users?.length > 0 ? (
                        <span className="flex items-center gap-1.5 text-xs text-ink-secondary">
                          <Users className="h-3.5 w-3.5" />
                          {org.org_users.length}
                        </span>
                      ) : (
                        <span className="text-xs text-ink-muted">—</span>
                      )}
                    </td>

                    <td className="px-5 py-3.5">
                      <Badge variant={STATUS_BADGE[org.status ?? ''] ?? 'neutral'}>
                        {hebrewLabel(org.status ?? '', ORG_STATUS_LABELS)}
                      </Badge>
                    </td>

                    <td className="hidden px-5 py-3.5 text-xs text-ink-muted lg:table-cell">
                      {formatRelative(org.created_at)}
                    </td>

                    <td className="px-3 py-3.5 text-center">
                      <RowActions org={org} onAction={setConfirm} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center justify-between border-t border-navy-border px-5 py-3">
            <span className="text-xs text-ink-muted">
              מציג {filtered.length} מתוך {orgsData?.total ?? 0}
            </span>
          </div>
        </div>
      )}

      {/* ─── Confirmation modal ────────────────────────────────────────── */}
      <Modal
        isOpen={confirm !== null}
        onClose={() => {
          if (confirmBusy) return;
          setConfirm(null);
          setErrorMsg(null);
        }}
        title={
          confirm?.kind === 'archive'
            ? 'העברה לארכיון'
            : confirm?.kind === 'unarchive'
              ? 'שחזור מארכיון'
              : confirm?.kind === 'delete'
                ? 'מחיקה לצמיתות'
                : ''
        }
        size="sm"
        footer={
          <>
            <button
              disabled={confirmBusy}
              onClick={() => {
                setConfirm(null);
                setErrorMsg(null);
              }}
              className="rounded-lg border border-navy-border bg-navy-card px-4 py-2 text-sm text-ink-secondary transition-colors hover:text-ink-primary disabled:opacity-50"
            >
              ביטול
            </button>
            <button
              disabled={confirmBusy}
              onClick={runConfirm}
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50',
                confirm?.kind === 'delete' ? 'bg-red-600 hover:bg-red-500' : 'bg-indigo-600 hover:bg-indigo-500'
              )}
            >
              {confirmBusy ? '…' : confirm?.kind === 'delete' ? 'מחק לצמיתות' : 'אישור'}
            </button>
          </>
        }
      >
        {confirm && (
          <div className="space-y-3 text-sm text-ink-secondary">
            {confirm.kind === 'archive' && (
              <p>
                להעביר את <span className="font-semibold text-ink-primary">{confirm.org.name}</span> לארכיון?
                הסטטוס ישתנה ל"לא פעיל" וניתן יהיה לשחזר בכל עת.
              </p>
            )}
            {confirm.kind === 'unarchive' && (
              <p>
                לשחזר את <span className="font-semibold text-ink-primary">{confirm.org.name}</span> מהארכיון?
              </p>
            )}
            {confirm.kind === 'delete' && (
              <>
                <p className="text-red-400">
                  פעולה בלתי הפיכה. הארגון <span className="font-semibold">{confirm.org.name}</span> יימחק לצמיתות.
                </p>
                <p>מותר רק כאשר אין משתמשים ואין מנויים פעילים. אם יש — העבר לארכיון במקום.</p>
              </>
            )}
            {errorMsg && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-400">
                {errorMsg}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
